import DateInput from '../../components/DateInput';
/**
 * Clinical Note (SOAP / DAP)
 *
 * Renders a discipline-aware visit note in the format the clinic admin selected.
 *  - SOAP: Subjective / Objective / Assessment / Plan (default — physio standard)
 *  - DAP:  Data / Assessment / Plan (common for osteopathy + some chiropractic)
 *
 * The exact set of fields shown is gated by the per-section toggles in
 * /admin/settings → Clinical examination format, so admins can hide sections
 * they don't use. Per-discipline section labels & placeholder hints adapt to
 * the appointment's discipline (Physio / Osteo / Chiro).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Save, Printer, FileText, ChevronLeft, Sparkles, Loader2, FileDown, Mail } from 'lucide-react';
import PrintSignatureBlock from '../../components/PrintSignatureBlock';
import AiScribeRecorder from '../../components/AiScribeRecorder';
import AiIcdSuggester from '../../components/AiIcdSuggester';
import QuickPhrasePicker, { appendPhrase } from '../../components/QuickPhrasePicker';
import useAutosave, { AutosaveIndicator } from '../../hooks/useAutosave';
import { DISCIPLINES, disciplineLabel } from '../../lib/disciplines';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

/* Per-discipline labels & placeholder hints. */
const DISCIPLINE_HINTS = {
  physiotherapy: {
    s: { label: 'Subjective', hint: 'Chief complaint, pain (VAS), aggravating / relieving factors, patient goals.' },
    o: { label: 'Objective',  hint: 'Inspection, palpation, AROM/PROM, MMT grades, special tests, functional measures.' },
    a: { label: 'Assessment', hint: 'Working diagnosis, clinical reasoning, ICD-10 / ICF codes, prognosis.' },
    p: { label: 'Plan',       hint: 'Modalities, exercise prescription, home program, frequency, follow-up.' },
    d: { label: 'Data (subjective + objective merged)', hint: 'Patient report + your physiotherapy findings combined.' },
    f: { label: 'Focus',      hint: 'The chief problem, concern, or significant event this entry addresses.' },
    act: { label: 'Action',   hint: 'Interventions performed: manual therapy, modalities, exercise prescription, education.' },
    r: { label: 'Response',   hint: 'Patient\'s response: pain VAS change, ROM gain, functional improvement, plan adjustment.' },
  },
  osteopathy: {
    s: { label: 'Subjective', hint: 'Presenting complaint, mechanism, lifestyle factors, sleep, hydration.' },
    o: { label: 'Objective',  hint: 'TART findings: Tissue texture, Asymmetry, Restriction, Tenderness. Postural / global motion.' },
    a: { label: 'Assessment', hint: 'Somatic dysfunction(s) identified, working impression, contributing systems (visceral / cranial).' },
    p: { label: 'Plan',       hint: 'MET / MFR / HVLA / cranial techniques used; home self-care; follow-up schedule.' },
    d: { label: 'Data (presentation + osteopathic findings)', hint: 'Patient story + TART findings + postural observations.' },
    f: { label: 'Focus',      hint: 'Primary somatic dysfunction or symptom this session addresses.' },
    act: { label: 'Action',   hint: 'MET / MFR / HVLA / cranial / counterstrain techniques performed.' },
    r: { label: 'Response',   hint: 'Post-treatment TART changes, symptom shift, motion restoration.' },
  },
  chiropractic: {
    s: { label: 'Subjective', hint: 'Chief complaint, OPQRST (Onset, Provocation, Quality, Radiation, Severity, Time).' },
    o: { label: 'Objective',  hint: 'Posture, motion palpation, segmental fixation, neurological screen, listing notation.' },
    a: { label: 'Assessment', hint: 'Subluxation / joint dysfunction listing, working diagnosis, ICD-10, response to care.' },
    p: { label: 'Plan',       hint: 'Adjustment (HVLA / drop / Activator), soft-tissue work, rehab, frequency.' },
    d: { label: 'Data (history + chiropractic exam findings)', hint: 'Patient history + adjusting findings.' },
    f: { label: 'Focus',      hint: 'Primary subluxation/listing or symptom focus for this visit.' },
    act: { label: 'Action',   hint: 'Adjustments delivered (technique, segment, side, audible/no audible).' },
    r: { label: 'Response',   hint: 'Symptom change, ROM, palpation post-check, patient tolerance.' },
  },
  massage_therapy: {
    s: { label: 'Subjective', hint: 'Areas of tension/pain, pressure preference, contraindications, treatment goals.' },
    o: { label: 'Objective',  hint: 'Palpation findings: hypertonicity, trigger points, adhesions, tissue quality, posture.' },
    a: { label: 'Assessment', hint: 'Soft-tissue dysfunction pattern, response to previous sessions, referral flags.' },
    p: { label: 'Plan',       hint: 'Techniques (Swedish / deep tissue / MFR / trigger point), areas, pressure, home care.' },
    d: { label: 'Data (client report + palpation findings)', hint: 'Client-reported tension + your palpation map.' },
    f: { label: 'Focus',      hint: 'Primary tension area or complaint this session addresses.' },
    act: { label: 'Action',   hint: 'Techniques applied per region, duration, pressure used.' },
    r: { label: 'Response',   hint: 'Tissue response, client feedback, post-session recommendations.' },
  },
  spa: {
    s: { label: 'Subjective', hint: 'Guest goals, skin/body concerns, sensitivities, allergies, relaxation preferences.' },
    o: { label: 'Objective',  hint: 'Skin/tissue observations, contra-indication screen, treatment suitability.' },
    a: { label: 'Assessment', hint: 'Suitability for treatment plan, product selection reasoning.' },
    p: { label: 'Plan',       hint: 'Treatments delivered (hydro / thermal / wrap / facial), products used, home advice.' },
    d: { label: 'Data', hint: 'Guest goals + observations combined.' },
    f: { label: 'Focus',      hint: 'Primary wellness goal for this visit.' },
    act: { label: 'Action',   hint: 'Treatments and products applied.' },
    r: { label: 'Response',   hint: 'Guest comfort, skin/tissue response, satisfaction.' },
  },
  podiatry: {
    s: { label: 'Subjective', hint: 'Foot/ankle complaint, footwear, activity, diabetes/vascular history, pain pattern.' },
    o: { label: 'Objective',  hint: 'Dermatological / vascular / neurological exam, gait, foot posture index, deformities.' },
    a: { label: 'Assessment', hint: 'Diagnosis (e.g. plantar fasciitis, neuropathy risk grade), ICD-10, risk stratification.' },
    p: { label: 'Plan',       hint: 'Debridement, offloading, orthoses, footwear advice, wound care, review interval.' },
    d: { label: 'Data (history + podiatric exam)', hint: 'Complaint + lower-limb exam findings.' },
    f: { label: 'Focus',      hint: 'Primary lesion/complaint this visit addresses.' },
    act: { label: 'Action',   hint: 'Procedures performed: debridement, nail care, padding, orthotic fitting.' },
    r: { label: 'Response',   hint: 'Immediate relief, tissue status, tolerance of procedure.' },
  },
  podology: {
    s: { label: 'Subjective', hint: 'Foot-care concerns, nail/skin complaints, footwear habits, medical flags.' },
    o: { label: 'Objective',  hint: 'Nail and skin condition, calluses, corns, fissures, hygiene status.' },
    a: { label: 'Assessment', hint: 'Non-medical foot-care findings; medical referral triggers if red flags.' },
    p: { label: 'Plan',       hint: 'Care performed (callus removal, nail trimming/correction), products, next visit.' },
    d: { label: 'Data', hint: 'Client concerns + observed foot condition.' },
    f: { label: 'Focus',      hint: 'Main foot-care need this visit.' },
    act: { label: 'Action',   hint: 'Foot-care procedures completed.' },
    r: { label: 'Response',   hint: 'Result, comfort, home-care advice given.' },
  },
  occupational_therapy: {
    s: { label: 'Subjective', hint: 'Occupational profile: roles, routines, ADL/IADL difficulties, environment, goals.' },
    o: { label: 'Objective',  hint: 'Performance-based assessment: ADL observation, standardized measures (COPM, FIM), cognition.' },
    a: { label: 'Assessment', hint: 'Occupational performance analysis, barriers/supports, progress toward goals.' },
    p: { label: 'Plan',       hint: 'Graded activities, adaptive equipment, environmental modification, caregiver education.' },
    d: { label: 'Data (occupational profile + performance findings)', hint: 'Client narrative + observed performance.' },
    f: { label: 'Focus',      hint: 'Occupational performance issue this session targets.' },
    act: { label: 'Action',   hint: 'Interventions: task training, splinting, equipment trial, education.' },
    r: { label: 'Response',   hint: 'Performance change, engagement, carryover to daily life.' },
  },
  exercise_physiology: {
    s: { label: 'Subjective', hint: 'Health history, medications, exercise history, readiness, perceived exertion trends.' },
    o: { label: 'Objective',  hint: 'Vitals, submax/max test results, strength & capacity measures, movement screen.' },
    a: { label: 'Assessment', hint: 'Physiological response interpretation, risk stratification, progression readiness.' },
    p: { label: 'Plan',       hint: 'FITT-VP prescription: frequency, intensity, time, type, volume, progression.' },
    d: { label: 'Data (history + physiological measures)', hint: 'Client report + measured responses.' },
    f: { label: 'Focus',      hint: 'Primary physiological target of this session.' },
    act: { label: 'Action',   hint: 'Exercise delivered, loads, sets/reps, monitoring performed.' },
    r: { label: 'Response',   hint: 'HR/BP/RPE response, tolerance, adjustments made.' },
  },
  fitness: {
    s: { label: 'Subjective', hint: 'Training goals, recovery, sleep, nutrition, motivation, injuries or niggles.' },
    o: { label: 'Objective',  hint: 'Session performance: loads, reps, times, movement quality, readiness scores.' },
    a: { label: 'Assessment', hint: 'Progress vs program, technique notes, load tolerance.' },
    p: { label: 'Plan',       hint: 'Next-session targets, progressive overload, mobility/recovery homework.' },
    d: { label: 'Data', hint: 'Client-reported readiness + session performance.' },
    f: { label: 'Focus',      hint: 'Primary training quality this session targets.' },
    act: { label: 'Action',   hint: 'Blocks completed, coaching cues given.' },
    r: { label: 'Response',   hint: 'Performance response, RPE, adjustments for next session.' },
  },
};

const DEFAULT_VISIT = {
  visit_date: new Date().toISOString().slice(0, 10),
  subjective: '', objective: '', assessment: '', plan: '',
  data: '',
  focus: '', action: '', response: '',
  therapies_performed: '', outcomes: '',
  format: 'SOAP', discipline: 'physiotherapy', appointment_id: null,
};

export default function ClinicalNote() {
  const { patient_id } = useParams();
  const [params] = useSearchParams();
  const appointmentId = params.get('appointment_id') || null;

  const [patient, setPatient] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_VISIT, patient_id, appointment_id: appointmentId });
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!patient_id) return;
    api.get(`/patients/${patient_id}`).then(r => setPatient(r.data)).catch(()=>{});
    api.get('/clinic/settings').then(r => {
      setSettings(r.data);
      // Seed the form with the clinic default format
      setForm(f => ({ ...f, format: r.data?.default_exam_format || 'SOAP' }));
    });
    // Try to inherit the discipline from the linked appointment (or most recent one).
    if (appointmentId) {
      api.get(`/appointments`).then(r => {
        const a = (r.data || []).find(x => x.appointment_id === appointmentId);
        if (a?.discipline) setForm(f => ({ ...f, discipline: a.discipline }));
      }).catch(()=>{});
    }
    // Iter 145 — auto-refill Subjective from Part 1 Subjective Examination
    // (patient interview). Only fills fields that are still empty so drafts
    // and manual entries are never overwritten.
    api.get(`/interviews/${patient_id}`).then(r => {
      const iv = r.data || {};
      if (!iv.chief_complaint && !iv.goals && !iv.aggravating_factors) return;
      const parts = [];
      if (iv.chief_complaint) parts.push(`Chief complaint: ${iv.chief_complaint}`);
      const pain = [];
      if (iv.pain_score != null) pain.push(`${iv.pain_score}/10`);
      if (iv.pain_quality) pain.push(iv.pain_quality);
      if (iv.pain_pattern) pain.push(iv.pain_pattern);
      if (pain.length) parts.push(`Pain: ${pain.join(', ')}`);
      if (iv.onset || iv.mechanism) parts.push(`Onset/mechanism: ${[iv.onset, iv.mechanism].filter(Boolean).join(' — ')}`);
      if (iv.aggravating_factors) parts.push(`Aggravating: ${iv.aggravating_factors}`);
      if (iv.relieving_factors) parts.push(`Relieving: ${iv.relieving_factors}`);
      if (iv.previous_treatment) parts.push(`Previous treatment: ${iv.previous_treatment}`);
      if (iv.medications) parts.push(`Medications: ${iv.medications}`);
      if (iv.goals) parts.push(`Patient goals: ${iv.goals}`);
      const text = parts.join('\n');
      if (!text) return;
      let filled = false;
      setForm(f => {
        if (f.subjective || f.data) return f;
        filled = true;
        return { ...f, subjective: text, data: text };
      });
      if (filled) toast.info('Subjective pre-filled from Part 1 patient interview — review and edit as needed.');
    }).catch(()=>{});
  }, [patient_id, appointmentId]);

  // Iter 69 — local autosave + resume-after-sleep. The clinical note often has
  // long S/O/A/P content; losing it to a sleeping phone is unacceptable.
  const { lastSavedAt, restore, clear } = useAutosave(`clinical-note:${patient_id}:${appointmentId || 'free'}`, form);

  useEffect(() => {
    const draft = restore();
    if (draft && typeof draft === 'object') {
      setForm(f => ({ ...f, ...draft, patient_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/visits', { ...form, patient_id });
      // Iter 74 — capture the visit_id from the response so we can offer
      // PDF download + email after saving (those endpoints need an id).
      if (data?.visit_id && !form.visit_id) {
        setForm(f => ({ ...f, visit_id: data.visit_id, record_no: data.record_no }));
      }
      clear();
      toast.success('Note saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // Iter 74 — PDF download + Email. Mirrors the massage flow.
  const [emailing, setEmailing] = useState(false);
  const downloadPdf = () => {
    if (!form.visit_id) { toast.error('Save the note first.'); return; }
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    window.open(`${base}/api/visits/${form.visit_id}/pdf`, '_blank');
  };
  const emailPdf = async () => {
    if (!form.visit_id) { toast.error('Save the note first.'); return; }
    if (!await safeConfirm(`Email the visit note to ${patient?.full_name || 'the patient'}?`)) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/visits/${form.visit_id}/send-email`);
      if (data?.resend_configured === false) {
        toast.info('Email service not configured — PDF rendered but not sent.');
      } else {
        toast.success(`Sent to ${data?.to}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    } finally { setEmailing(false); }
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post('/ai/treatment-suggest', { patient_id, discipline: form.discipline });
      if (form.format === 'DAP') {
        set('data', [data.diagnosis, data.plan_of_action].filter(Boolean).join('\n\n'));
        set('assessment', data.diagnosis || '');
        set('plan', [data.treatment, data.exercise].filter(Boolean).join('\n\n'));
      } else {
        set('assessment', data.diagnosis || '');
        set('plan', [data.plan_of_action, data.treatment, data.exercise].filter(Boolean).join('\n\n'));
      }
      toast.success('AI draft inserted — please review before saving.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI failed.');
    } finally { setAiBusy(false); }
  };

  if (!settings) return <div className="p-8">Loading…</div>;

  const fmt = ['SOAP', 'DAP', 'FDAR'].includes(form.format) ? form.format : 'SOAP';
  const hints = DISCIPLINE_HINTS[form.discipline] || DISCIPLINE_HINTS.physiotherapy;
  const showSection = (k) => settings[`${fmt.toLowerCase()}_section_${k}`] !== false;
  const enabledDisciplines = settings.service_disciplines || ['physiotherapy'];

  return (
    <div className="p-4 md:p-8 max-w-5xl print:p-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6 print:mb-4">
        <div>
          <Link to={`/admin/patients/${patient_id}`} className="text-xs text-fg-muted inline-flex items-center gap-1 print:hidden hover:text-brand" data-testid="back-to-chart">
            <ChevronLeft className="w-3 h-3"/> Back to chart
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mt-2 mb-1">CLINICAL NOTE</div>
          <h1 key={form.format} className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="note-h1">{`${form.format === 'DAP' ? 'DAP' : form.format === 'FDAR' ? 'F-DAR' : 'SOAP'} note`}</h1>
          {patient && <p className="text-fg-muted mt-2 text-sm">{patient.full_name} · {form.visit_date}</p>}
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          {settings.feature_ai_scribe && (
            <AiScribeRecorder
              patientId={patient_id}
              appointmentId={appointmentId}
              format={form.format}
              discipline={form.discipline}
              onDraft={(data) => {
                if (form.format === 'DAP') {
                  if (data?.draft?.data) set('data', data.draft.data);
                  if (data?.draft?.assessment) set('assessment', data.draft.assessment);
                  if (data?.draft?.plan) set('plan', data.draft.plan);
                } else {
                  if (data?.draft?.subjective) set('subjective', data.draft.subjective);
                  if (data?.draft?.objective) set('objective', data.draft.objective);
                  if (data?.draft?.assessment) set('assessment', data.draft.assessment);
                  if (data?.draft?.plan) set('plan', data.draft.plan);
                }
              }}
            />
          )}
          <button onClick={runAi} disabled={aiBusy} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="note-ai-draft">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
            {aiBusy ? 'Drafting…' : 'AI draft'}
          </button>
          <button onClick={()=>window.print()} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="note-print">
            <Printer className="w-4 h-4"/> Print
          </button>
          {/* Iter 74 — PDF download + Email-to-patient. The visit must be saved
              first (no visit_id → server can't render). */}
          {form.visit_id && (
            <>
              <button onClick={downloadPdf}
                      className="btn-secondary inline-flex items-center gap-2 text-sm"
                      data-testid="note-pdf-download">
                <FileDown className="w-4 h-4"/> PDF
              </button>
              <button onClick={emailPdf} disabled={emailing}
                      className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                      data-testid="note-pdf-email">
                {emailing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
                Email patient
              </button>
            </>
          )}
          <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="note-save">
            <Save className="w-4 h-4"/> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Top metadata row — discipline + format pickers + visit date. */}
      <div className="bento-card mb-4 print:mb-2">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Visit date">
            <DateInput value={form.visit_date}
                   onChange={e=>set('visit_date', e.target.value)} data-testid="note-date"/>
          </Field>
          <Field label="Discipline">
            <select className="input" value={form.discipline} onChange={e=>set('discipline', e.target.value)} data-testid="note-discipline">
              <optgroup label="Your clinic's disciplines">
                {enabledDisciplines.map(d => <option key={d} value={d}>{disciplineLabel(d)}</option>)}
              </optgroup>
              {DISCIPLINES.filter(d => !enabledDisciplines.includes(d.key)).length > 0 && (
                <optgroup label="All disciplines">
                  {DISCIPLINES.filter(d => !enabledDisciplines.includes(d.key)).map(d => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
          <Field label="Format">
            <div className="flex gap-2" data-testid="note-format-picker">
              {['SOAP', 'DAP', 'FDAR'].map(f => (
                <button key={f} type="button" onClick={()=>set('format', f)}
                        className={`flex-1 px-3 py-2 rounded-md border text-sm transition ${form.format===f?'bg-brand text-white border-brand':'border-soft hover:border-brand/40'}`}
                        data-testid={`note-fmt-${f.toLowerCase()}`}>
                  {f}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </div>

      {/* Body — SOAP / DAP / F-DAR.
          NOTE: `key={fmt}` forces a full subtree remount when the format flips.
          Without it the host's visual-editor instrumentation (data-ve-dynamic spans
          with x-excluded="true") caches the previous branch and refuses to swap. */}
      {fmt === 'SOAP' && (
        <div className="space-y-3" key="soap-body">
          {showSection('subjective') && (
            <Block letter="S" title={hints.s.label} hint={hints.s.hint} testid="note-s"
                   onPickPhrase={(t) => set('subjective', appendPhrase(form.subjective, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[110px]" value={form.subjective}
                        onChange={e=>set('subjective', e.target.value)} data-testid="note-subjective"/>
            </Block>
          )}
          {showSection('objective') && (
            <Block letter="O" title={hints.o.label} hint={hints.o.hint} testid="note-o"
                   onPickPhrase={(t) => set('objective', appendPhrase(form.objective, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[140px]" value={form.objective}
                        onChange={e=>set('objective', e.target.value)} data-testid="note-objective"/>
            </Block>
          )}
          {showSection('assessment') && (
            <Block letter="A" title={hints.a.label} hint={hints.a.hint} testid="note-a"
                   onPickPhrase={(t) => set('assessment', appendPhrase(form.assessment, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[110px]" value={form.assessment}
                        onChange={e=>set('assessment', e.target.value)} data-testid="note-assessment"/>
              {/* Iter 73 — AI ICD suggester. Reads the clinician's assessment
                  text and proposes top-3 codes from the 1,280+ canonical list. */}
              <div className="mt-2">
                <AiIcdSuggester text={form.assessment} patientId={patient_id}
                                onPick={(s) => set('assessment',
                                  appendPhrase(form.assessment, `[${s.code} — ${s.name}]`))}
                                testidPrefix="note-ai-icd"/>
              </div>
            </Block>
          )}
          {showSection('plan') && (
            <Block letter="P" title={hints.p.label} hint={hints.p.hint} testid="note-p"
                   onPickPhrase={(t) => set('plan', appendPhrase(form.plan, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[110px]" value={form.plan}
                        onChange={e=>set('plan', e.target.value)} data-testid="note-plan"/>
            </Block>
          )}
        </div>
      )}
      {fmt === 'DAP' && (
        <div className="space-y-3" key="dap-body">
          {showSection('data') && (
            <Block letter="D" title={hints.d.label} hint={hints.d.hint} testid="note-d"
                   onPickPhrase={(t) => set('data', appendPhrase(form.data, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[180px]" value={form.data}
                        onChange={e=>set('data', e.target.value)} data-testid="note-data"/>
            </Block>
          )}
          {showSection('assessment') && (
            <Block letter="A" title={hints.a.label} hint={hints.a.hint} testid="note-a"
                   onPickPhrase={(t) => set('assessment', appendPhrase(form.assessment, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[110px]" value={form.assessment}
                        onChange={e=>set('assessment', e.target.value)} data-testid="note-assessment"/>
              <div className="mt-2">
                <AiIcdSuggester text={form.assessment} patientId={patient_id}
                                onPick={(s) => set('assessment',
                                  appendPhrase(form.assessment, `[${s.code} — ${s.name}]`))}
                                testidPrefix="note-ai-icd-dap"/>
              </div>
            </Block>
          )}
          {showSection('plan') && (
            <Block letter="P" title={hints.p.label} hint={hints.p.hint} testid="note-p"
                   onPickPhrase={(t) => set('plan', appendPhrase(form.plan, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[110px]" value={form.plan}
                        onChange={e=>set('plan', e.target.value)} data-testid="note-plan"/>
            </Block>
          )}
        </div>
      )}
      {fmt === 'FDAR' && (
        <div className="space-y-3" key="fdar-body">
          {showSection('focus') && (
            <Block letter="F" title={hints.f.label} hint={hints.f.hint} testid="note-f"
                   onPickPhrase={(t) => set('focus', appendPhrase(form.focus, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[80px]" value={form.focus}
                        onChange={e=>set('focus', e.target.value)} data-testid="note-focus"
                        placeholder="e.g. Right shoulder pain limiting overhead reach"/>
            </Block>
          )}
          {showSection('data') && (
            <Block letter="D" title={hints.d.label} hint={hints.d.hint} testid="note-d"
                   onPickPhrase={(t) => set('data', appendPhrase(form.data, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[140px]" value={form.data}
                        onChange={e=>set('data', e.target.value)} data-testid="note-data"/>
            </Block>
          )}
          {showSection('action') && (
            <Block letter="A" title={hints.act.label} hint={hints.act.hint} testid="note-act"
                   onPickPhrase={(t) => set('action', appendPhrase(form.action, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[120px]" value={form.action}
                        onChange={e=>set('action', e.target.value)} data-testid="note-action"/>
            </Block>
          )}
          {showSection('response') && (
            <Block letter="R" title={hints.r.label} hint={hints.r.hint} testid="note-r"
                   onPickPhrase={(t) => set('response', appendPhrase(form.response, t))}
                   discipline={form.discipline}>
              <textarea className="input min-h-[100px]" value={form.response}
                        onChange={e=>set('response', e.target.value)} data-testid="note-response"
                        placeholder="e.g. VAS reduced 7/10 → 4/10; flexion +20°; patient tolerated session well."/>
            </Block>
          )}
        </div>
      )}

      {/* Print footer + signature block. */}
      <div className="hidden print:block text-xs text-fg-muted mt-6 pt-3 border-t border-soft">
        Physiogenic · {fmt} clinical note · {form.discipline} · Generated {new Date().toLocaleString()}
      </div>
      <PrintSignatureBlock leftLabel="Clinician / Provider" rightLabel="Patient"/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function Block({ letter, title, hint, children, testid, onPickPhrase, discipline }) {
  return (
    <div className="bento-card" data-testid={testid}>
      <div className="flex items-start gap-3 mb-2">
        <div className="shrink-0 w-9 h-9 rounded-md bg-brand text-white font-heading font-bold flex items-center justify-center text-lg">
          {letter}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand"/> {title}
            </h3>
            {onPickPhrase && (
              <QuickPhrasePicker
                discipline={discipline || 'physiotherapy'}
                onPick={onPickPhrase}
                testid={`${testid}-phrases`}
              />
            )}
          </div>
          <p className="text-xs text-fg-muted mt-0.5">{hint}</p>
        </div>
      </div>
      {children}
    </div>
  );
}
